import React, { useState } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import LinkItem from 'components/LinkItem';
import { IMenuItemConfig } from 'containers/App/navConfig';
import {
    getNavLabel,
    getVisibleSubItems,
    isNavItemVisibleWithChildren,
    isPathMatch
} from 'lib/navigation';
import _ from 'lodash';

export interface INavigationFactoryContext {
    expanded: boolean;
    isDesktop: boolean;
    isPhone: boolean;
    activeKey: string;
    additionalTabName: string;
    user: any;
    location: ReturnType<typeof useLocation>;
    setShowUsersDropdown?: (val: boolean) => void;
    showUsersDropdown?: boolean;
    setShowTermsDropdown?: (val: boolean) => void;
    showTermsDropdown?: boolean;
}

interface INavigationFactoryItemsProps {
    items: IMenuItemConfig[];
    context: INavigationFactoryContext;
}

const NavigationFactoryItems = ({ items, context }: INavigationFactoryItemsProps) => {
    const { expanded, isDesktop, isPhone, activeKey, additionalTabName, user, location } = context;

    const [dropdownStates, setDropdownStates] = useState<Record<string, boolean>>({});

    const toggleDropdown = (id: string) => {
        setDropdownStates((prev) => {
            const newState = { ...prev, [id]: !prev[id] };

            if (id === 'promotions-users' && newState[id]) {
                newState['promotions-terms'] = false;
            } else if (id === 'promotions-terms' && newState[id]) {
                newState['promotions-users'] = false;
            }
            return newState;
        });
    };

    const navContext = { user, isPhone, additionalTabName };

    const getLabel = (config: IMenuItemConfig) => getNavLabel(config, additionalTabName);

    const visibleItems = _.filter(items, (item) => isNavItemVisibleWithChildren(item, navContext));

    const renderDropdownItem = (item: IMenuItemConfig, parentItemId: string) => {
        if (!isNavItemVisibleWithChildren(item, navContext)) return null;

        const checkIsActive = (targetPath: string) => isPathMatch(location.pathname, targetPath, process.env.PUBLIC_URL || '');

        if (item.mobileSubmenuContainer) {
            const visibleSubItems = getVisibleSubItems(item, navContext);
            const isSubmenuActive = _.some(item.subItems, (el) => isPathMatch(location.pathname, el.path, process.env.PUBLIC_URL || ''));
            const isOpen = dropdownStates[item.id] || false;

            return (
                <NavDropdown
                    key={item.id}
                    renderMenuOnMount
                    title={getLabel(item)}
                    id={`${item.id}-mobile-dropdown`}
                    className={`App-navDropdown-submenu ${isSubmenuActive ? 'App-navDropdown-submenu-active' : ''}`}
                    show={isOpen}
                    onClick={(e) => { e.stopPropagation(); toggleDropdown(item.id); }}
                >
                    {_.map(visibleSubItems, (subItem, index) => (
                        <React.Fragment key={subItem.id}>
                            <NavDropdown.Item
                                className={checkIsActive(subItem.path) ? 'App-navDropdownItem-submenu-active' : 'App-navDropdownItem'}
                                as={Link}
                                to={`${process.env.PUBLIC_URL}${subItem.path}`}
                                eventKey={subItem.id}
                                onClick={() => setDropdownStates({})}
                            >
                                {getLabel(subItem)}
                            </NavDropdown.Item>
                            {expanded && index < visibleSubItems.length - 1 && <hr />}
                        </React.Fragment>
                    ))}
                </NavDropdown>
            );
        }

        return (
            <NavDropdown.Item
                key={item.id}
                className={checkIsActive(item.path) ? 'App-navDropdownItem-active' : 'App-navDropdownItem'}
                as={Link}
                to={`${process.env.PUBLIC_URL}${item.path}`}
                eventKey={item.id}
                onClick={() => setDropdownStates({})}
            >
                {getLabel(item)}
            </NavDropdown.Item>
        );
    };

    const renderItem = (item: IMenuItemConfig, index: number) => {
        const isDropdownOnMobile = item.showAsDropdownOnMobile && !isDesktop && (item.subItems && item.subItems.length > 0);
        const isActiveTab = activeKey === item.id;

        let content: React.ReactNode;

        if (isDropdownOnMobile) {
            const visibleSubItems = getVisibleSubItems(item, navContext);
            const isOpen = dropdownStates[item.id] || false;

            const isNavDropdownActive = isPathMatch(location.pathname, item.path, process.env.PUBLIC_URL || '') || isActiveTab;
            const hasSubmenusOpen = _.some(visibleSubItems, (si) => dropdownStates[si.id]);

            content = (
                <NavDropdown
                    renderMenuOnMount
                    title={getLabel(item)}
                    id={`${item.id}-dropdown`}
                    className={`
                        ${isNavDropdownActive ? 'App-navDropdown-active' : (isOpen ? 'App-navDropdown-show' : 'App-navDropdown')}
                        ${hasSubmenusOpen ? 'last-item-open' : ''}
                    `}
                    show={isOpen}
                    onClick={() => toggleDropdown(item.id)}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        {_.map(visibleSubItems, (subItem, idx) => {
                            const renderedSubItem = renderDropdownItem(subItem, item.id);
                            if (!renderedSubItem) return null;

                            // check if it's the last item to avoid rendering an hr
                            const isLast = idx === visibleSubItems.length - 1;
                            const isSubmenuContainer = subItem.mobileSubmenuContainer;

                            return (
                                <React.Fragment key={subItem.id}>
                                    {renderedSubItem}
                                    {expanded && !isLast && (!isSubmenuContainer || !dropdownStates[subItem.id]) && <hr className='App-Mobile-Navigation' />}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </NavDropdown>
            );
        } else {
            content = (
                <LinkItem
                    to={`${process.env.PUBLIC_URL}${item.path}`}
                    textOnLeft
                    isActive={isActiveTab}
                    eventKey={item.id}
                >
                    {getLabel(item)}
                </LinkItem>
            );
        }

        return (
            <React.Fragment key={item.id}>
                {content}
                {expanded && index < visibleItems.length - 1 && <hr />}
            </React.Fragment>
        );
    };

    return (
        <>
            {_.map(visibleItems, (item, index) => renderItem(item, index))}
        </>
    );
};

export default NavigationFactoryItems;
